import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { API, api, errMsg } from "@/lib/api";
import { BigCheck, StepNav } from "@/components/wizard/Stepper";
import { SidewaysHelp } from "@/components/wizard/SidewaysHelp";

const CHUNK = 4 * 1024 * 1024;
const MAX_MB = 300;

const withRetry = async (fn, tries = 3) => {
  for (let n = 1; ; n++) {
    try { return await fn(); } catch (e) {
      if (n >= tries || (e.response && e.response.status < 500 && e.response.status !== 429)) throw e;
      await new Promise((r) => setTimeout(r, 1500 * n));
    }
  }
};

const readDuration = (file) =>
  new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve({ d: v.duration, w: v.videoWidth, h: v.videoHeight }); };
    v.onerror = () => resolve(null);
    v.src = URL.createObjectURL(file);
  });

export const OrderVideo = ({ order, className = "" }) => (
  <div className={`polaroid mx-auto w-full max-w-[260px] rotate-[-1.5deg] ${className}`}>
    <video key={order.video?.filename + order.updated_at} src={`${API}/orders/${order.order_id}/video`} controls playsInline preload="metadata" className="aspect-[9/16] w-full bg-black object-contain" data-testid="order-video-player" />
    <p className="font-script mt-2 truncate text-center text-xl text-[#4A3B32]">{order.title || order.video?.filename}</p>
  </div>
);

export const StepVideo = ({ order, setOrder, go }) => {
  const input = useRef(null);
  const [pct, setPct] = useState(null);
  const [owns, setOwns] = useState(!!order.consents?.owns_video);
  const [noCopy, setNoCopy] = useState(!!order.consents?.no_copyright);
  const [sideways, setSideways] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    const meta = await readDuration(file);
    if (meta && meta.d > 180.5) return toast.error(`This video is ${Math.floor(meta.d / 60)} min ${Math.floor(meta.d % 60)} sec. Please choose one under 3 minutes.`);
    setSideways(false);
    if (meta && meta.w > meta.h) {
      if (input.current) input.current.value = "";
      return setSideways(true);
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      if (input.current) input.current.value = "";
      return toast.error(`This video is ${Math.ceil(file.size / 1048576)} MB, over the ${MAX_MB} MB limit. Please choose a shorter video, or film at 1080p instead of 4K.`);
    }
    setPct(0);
    window.__v2rUploading = true;
    try {
      const uploadId = `up_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", String(i));
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
        await withRetry(() => api.post(`/orders/${order.order_id}/video/chunk`, fd, {
          onUploadProgress: (e) => setPct(Math.min(95, Math.round(((i * CHUNK + e.loaded) / file.size) * 95))),
        }));
      }
      const { data } = await api.post(`/orders/${order.order_id}/video/complete`, { upload_id: uploadId, total, filename: file.name, content_type: file.type || "" });
      setPct(100);
      setOrder(data);
      toast.success("Your video is uploaded.");
    } catch (e) {
      if (/sideways|landscape|portrait/i.test(errMsg(e))) setSideways(true);
      else toast.error(errMsg(e));
    } finally {
      window.__v2rUploading = false;
      setPct(null);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div>
      <h2 className="text-3xl font-bold text-[#4A2A18]">Upload your video</h2>
      <p className="mt-3 text-lg text-[#4A3B32]">Choose a video filmed upright on your phone, <b>up to 3 minutes</b> long.</p>
      <div className="mt-5 flex items-center gap-4 rounded-xl border-2 border-[#E6DEC6] bg-white p-4" data-testid="video-format-tip">
        <span className="flex h-16 w-10 shrink-0 items-center justify-center rounded-md border-[3px] border-[#A83232] text-xs font-bold text-[#A83232]">9:16</span>
        <p className="text-lg text-[#4A3B32]"><b>Vertical format only.</b> Hold your phone upright (portrait mode) while filming, so the video is tall, not wide. This is the <b>9:16 format</b> used for YouTube Shorts. <a href="/tips" target="_blank" rel="noreferrer" className="font-semibold text-[#A83232] underline" data-testid="video-tips-link">Filming tips</a></p>
      </div>
      <div className="mt-8 grid gap-8 md:grid-cols-2 md:items-start">
        <div>
          <input ref={input} type="file" accept="video/mp4,video/quicktime,video/x-m4v,video/webm,.mov,.mp4,.m4v" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="video-file-input" />
          <button type="button" className="flex w-full flex-col items-center gap-3 rounded-2xl border-4 border-dashed border-[#C86446] bg-white px-6 py-10 text-center transition-colors hover:bg-[#FFF8F3]" onClick={() => input.current?.click()} disabled={pct !== null} data-testid="video-upload-button">
            <UploadCloud size={48} className="text-[#A83232]" />
            <span className="text-xl font-bold text-[#2C1E16]">{order.video ? "Choose a different video" : "Tap to choose your video"}</span>
            <span className="text-base text-[#5C4A3E]">Vertical (portrait, 9:16) MP4 or MOV, under 3 minutes, up to 300 MB</span>
          </button>
          {sideways && <SidewaysHelp onRetry={() => input.current?.click()} onClose={() => setSideways(false)} />}
          {pct !== null && (
            <div className="mt-5" data-testid="video-upload-progress">
              <p className="mb-2 text-lg font-semibold">{pct < 100 ? `Uploading... ${pct}%` : "Checking your video..."}</p>
              <Progress value={pct} className="h-4" />
            </div>
          )}
          {order.video && (
            <p className="mt-5 text-lg text-[#2E6930]" data-testid="video-uploaded-info">
              Uploaded: {order.video.filename} ({Math.floor(order.video.duration / 60)}:{String(Math.round(order.video.duration % 60)).padStart(2, "0")})
            </p>
          )}
        </div>
        {order.video && <OrderVideo order={order} />}
      </div>
      <div className="mt-8">
        <BigCheck checked={owns} onChange={setOwns} testid="consent-owns-video-checkbox">
          I own this video, or I have permission from everyone in it to share it.
        </BigCheck>
        <div className="mt-4">
          <BigCheck checked={noCopy} onChange={setNoCopy} testid="consent-no-copyright-checkbox">
            My video has no copyrighted music, movies or pictures. <span className="text-[#5C4A3E]">(YouTube may block videos that do.)</span>
          </BigCheck>
        </div>
      </div>
      <StepNav next={() => go(2, { consent_owns_video: owns, consent_no_copyright: noCopy })} disabled={!order.video || !owns || !noCopy || pct !== null} />
    </div>
  );
};
